import React, { useState } from 'react';
import { useMutation, useQuery } from '@common/hooks';
import { createMessage } from 'works/legacy/lib/message-helper';
import {
  Button,
  Col,
  Divider,
  Form,
  Row,
  Select,
  Space,
  UploadFile,
} from 'antd';
import {
  DISPUTE_REJECTION_OPTIONS_QUERY,
  DisputeReviewFragmentType,
  PerformDisputeAction,
} from '@pay-bill/graphql';
import {
  DisputeCommentInput,
  DisputeFilesInput,
  DisputeFilesSection,
  DrawerBody,
  DrawerFooter,
  DrawerTitle,
} from '@pay-bill/components';
import { useDisputeDeletedFiles, useDisputeRefetchs } from '@pay-bill/hooks';
import { catchErrorWith, rejectSimpleErrors } from '@pay-bill/services';

type FormValues = {
  comment?: string;
  rejectionReason?: string;
  files: UploadFile[];
};

export function DisputeRejectionDrawerBody({
  dispute,
  goBack,
}: {
  dispute: DisputeReviewFragmentType;
  goBack: () => void;
}) {
  const [commentRequired, setCommentRequired] = useState(false);
  const [removedFiles, onFileDelete, maxFilesSlots] = useDisputeDeletedFiles(
    dispute.files.organization
  );
  const [formValues, setFormValues] = useState<FormValues>(() => ({
    files: [],
  }));

  const [performAction, { loading: loadingPerform }] =
    useMutation(PerformDisputeAction);

  const options = useQuery(DISPUTE_REJECTION_OPTIONS_QUERY);

  const [form] = Form.useForm();
  const refetchQueries = useDisputeRefetchs();

  // FIXME: antd upgrade compatibility, was Record<string, any> before.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onFinish = (values: Record<string, any>) => {
    if (loadingPerform) {
      return;
    }

    performAction({
      variables: {
        disputeId: dispute.id,
        input: {
          action: 'reject',
          rejectionReason: values.rejectionReason,
          comment: values.comment,
          addedFiles: values.files || [],
          removedFiles: removedFiles.map((file) => file.id),
        },
      },
    })
      .then(rejectSimpleErrors('performDisputeAction'))
      .then(() => {
        createMessage('success', 'Dispute rejected');
        refetchQueries();
        goBack();
      })
      .catch(catchErrorWith('Failed to reject dispute. Please try again.'));
  };

  const allFieldsFilledIn =
    formValues.rejectionReason === 'other'
      ? !!(formValues.rejectionReason && formValues.comment)
      : !!formValues.rejectionReason;

  const reasons = options.data?.options.rejectionReasons || [];
  const checkCommentRequired = (value: string) => {
    const found = reasons.find((reason) => reason.id === value);

    setCommentRequired(!!found && found.commentRequired);
  };

  return (
    <>
      <DrawerTitle>Reject Dispute</DrawerTitle>
      <DrawerBody>
        <div className="rejection-instructions">
          Please select a reason for rejecting this dispute.
        </div>
        <div>
          <Form
            onFinish={onFinish}
            form={form}
            layout="vertical"
            initialValues={formValues}
            onValuesChange={(changes) => {
              setFormValues({ ...formValues, ...changes });
            }}
          >
            <Form.Item
              label="Rejection Reason"
              name="rejectionReason"
              rules={[{ required: true, message: 'Please select a reason' }]}
            >
              <Select
                size="large"
                placeholder="Please select a reason"
                onChange={checkCommentRequired}
              >
                {reasons.map((reason) => (
                  <Select.Option key={reason.id} value={reason.id}>
                    {reason.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <DisputeCommentInput required={commentRequired} />
            <Row className="dispute-upload-row">
              <Col span={24}>
                <DisputeFilesInput maxFilesSlots={maxFilesSlots} />
              </Col>
            </Row>
            <Divider />
            <DisputeFilesSection
              dispute={dispute}
              canDeleteFiles="Organization"
              onDelete={onFileDelete}
              hiddenFiles={removedFiles}
            />
          </Form>
        </div>
      </DrawerBody>
      <DrawerFooter>
        <Space>
          <Col span={9}>
            <Button
              className="dispute-edit-button"
              type="default"
              size="large"
              onClick={goBack}
            >
              Back
            </Button>
          </Col>
          <Col span={9}>
            <Button
              type="primary"
              danger={true}
              size="large"
              onClick={form.submit}
              disabled={!allFieldsFilledIn}
              id="dispute-rejection-button"
            >
              Reject
            </Button>
          </Col>
        </Space>
      </DrawerFooter>
      <style jsx>
        {`
          .rejection-instructions {
            margin-bottom: 40px;
          }
          :global(.dispute-upload-row) {
            margin-top: 8px;
            max-width: 100%;
          }
        `}
      </style>
    </>
  );
}
